import { Banner, Box, Button, Group, Input, Text } from '@optiaxiom/react';
import { IconCheck, IconCheckCircleSolid, IconCopy, IconEye, IconEyeSlash, IconFileLines } from '@optiaxiom/icons';
import { useState } from 'react';
import { AppDialog } from '@/components/ui/AppDialog';
import { useCopyToClipboard } from '@/lib/useCopyToClipboard';
import { maskKey } from './maskKey';
import styles from './GeneratedKeyModal.module.css';

interface GeneratedKeyModalProps {
  open: boolean;
  /** The plaintext key to reveal, or `null` when the dialog should be closed. */
  apiKey: string | null;
  onClose: () => void;
}

/**
 * Shows a freshly generated API key exactly once. The key can never be retrieved
 * again, so the dialog surfaces it in full with a one-click copy and reminds the
 * developer to store it safely (e.g. in `~/.ocp/credentials.json` for the CLI).
 */
export function GeneratedKeyModal({ open, apiKey, onClose }: GeneratedKeyModalProps) {
  const { copied, copy } = useCopyToClipboard();
  const [revealed, setRevealed] = useState(false);

  // Reset the reveal toggle on close so the next key starts masked.
  const handleClose = () => {
    setRevealed(false);
    onClose();
  };

  const shownKey = apiKey ? (revealed ? apiKey : maskKey(apiKey)) : '';

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Your API key is ready"
      titleIcon={<IconCheckCircleSolid size={20} />}
      footer={
        <Button appearance="default" onClick={handleClose}>
          Done
        </Button>
      }
    >
      <Group flexDirection="column" gap="20" alignItems="stretch">
        <Banner intent="information" rounded="lg">
          <Group flexDirection="column" gap="2">
            <Text>Save your API key in a safe place.</Text>
            <Text>You won&rsquo;t be able to view it again.</Text>
          </Group>
        </Banner>

        <Group flexDirection="column" gap="6" alignItems="stretch">
          <Text fontWeight="600">API Key</Text>
          <Group alignItems="center" gap="8">
            <Input readOnly value={shownKey} aria-label="API key" className={styles.keyInput} />
            <Button
              appearance="subtle"
              icon={revealed ? <IconEyeSlash /> : <IconEye />}
              aria-label={revealed ? 'Hide key' : 'Show key'}
              title={revealed ? 'Hide key' : 'Show key'}
              onClick={() => setRevealed((v) => !v)}
              className={styles.copyButton}
            />
            <Button
              appearance="default"
              addonBefore={copied ? <IconCheck /> : <IconCopy />}
              onClick={() => apiKey && void copy(apiKey)}
              className={styles.copyButton}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Group>
        </Group>

        <Group alignItems="start" gap="12">
          <Box className={styles.noteIcon}>
            <IconFileLines size={20} />
          </Box>
          <Group flexDirection="column" gap="8">
            <Text fontWeight="600">Keep your key secure</Text>
            <Box asChild className={styles.bullets}>
              <ul>
                <li>
                  Save it to <code className={styles.inlineCode}>~/.ocp/credentials.json</code> as{' '}
                  <code className={styles.inlineCode}>{'{ "apiKey": "…" }'}</code> for the OCP CLI.
                </li>
                <li>Don&rsquo;t commit it to source control or share it with anyone.</li>
                <li>Prefer environment variables in CI and shared environments.</li>
              </ul>
            </Box>
          </Group>
        </Group>
      </Group>
    </AppDialog>
  );
}
